import * as LucideIcons from 'lucide-react'
import { LucideIcon } from 'lucide-react'
import { Tab } from './useTabManager'
import { DailyIcon } from '../../components/icons/DailyIcon'
import { DormantIcon, DormantOpenIcon } from '../../components/icons/DormantIcon'
import { TopicsIcon } from '../../components/icons/TopicsIcon'
import { HabitsIcon } from '../../components/icons/HabitsIcon'
import { GoalsIcon } from '../../components/icons/GoalsIcon'
import { AreaIcon } from '../../components/icons/AreaIcon'
import { ProjectIcon } from '../../components/icons/ProjectIcon'
import { RecurringIcon } from '../../components/icons/RecurringIcon'
import { RocketIcon } from '../../components/icons/RocketIcon'
import { SproutIcon } from '../../components/icons/SproutIcon'
import { UpcomingIcon, UpcomingOpenIcon } from '../../components/icons/UpcomingIcon'
import {
  QuickNotesFilledIcon,
  QuickNotesEmptyIcon,
  QuickNotesFilledOpenIcon,
  QuickNotesEmptyOpenIcon,
} from '../../components/icons/QuickNotesIcon'

// Re-export shared type for backward compatibility
export type { SerializedTab } from '@/src/shared/types/tabs'
import type { SerializedTab } from '@/src/shared/types/tabs'
import type { TabIcon } from '../../hooks/multiWindow'

// Custom (non-Lucide) icon registry
const customIcons: Record<string, React.ComponentType<{ size?: number }>> = {
  AreaIcon,
  DailyIcon,
  GoalsIcon,
  HabitsIcon,
  DormantIcon,
  DormantOpenIcon,
  ProjectIcon,
  RecurringIcon,
  RocketIcon,
  SproutIcon,
  TopicsIcon,
  UpcomingIcon,
  UpcomingOpenIcon,
  QuickNotesFilledIcon,
  QuickNotesEmptyIcon,
  QuickNotesFilledOpenIcon,
  QuickNotesEmptyOpenIcon,
}

// Global icon caches shared with useTabPersistence (same module-level singletons)
const iconNameCache = new Map<TabIcon, string>()
const iconComponentCache = new Map<string, LucideIcon>()

/**
 * Resolves a Lucide icon component to its string name.
 *
 * @description
 * Performs a reverse lookup against the full Lucide icon registry.
 * Results are cached in a module-level `Map` — first call is O(n),
 * subsequent calls for the same component are O(1).
 *
 * @param icon - A Lucide icon component (e.g. `CalendarDays`)
 * @returns The icon's string name (e.g. `'CalendarDays'`), or `undefined` if not found
 */
export function getIconName(icon?: TabIcon): string | undefined {
  if (!icon) return undefined

  if (iconNameCache.has(icon)) {
    return iconNameCache.get(icon)
  }

  // Check custom icons first
  const customEntry = Object.entries(customIcons).find(([_, comp]) => comp === (icon as unknown))
  if (customEntry) {
    iconNameCache.set(icon, customEntry[0])
    return customEntry[0]
  }

  const entry = Object.entries(LucideIcons).find(([_, comp]) => comp === icon)
  const name = entry?.[0]

  if (name) {
    iconNameCache.set(icon, name)
  }

  return name
}

/**
 * Resolves an icon name string to its Lucide icon component.
 *
 * @description
 * Looks up the component by name from the full Lucide icon registry.
 * Results are cached in a module-level `Map` for O(1) subsequent lookups.
 *
 * @param iconName - Lucide icon name (e.g. `'CalendarDays'`)
 * @returns The corresponding icon component, or `undefined` if not found
 */
export function getIconComponent(iconName?: string): LucideIcon | undefined {
  if (!iconName) return undefined

  if (iconComponentCache.has(iconName)) {
    return iconComponentCache.get(iconName)
  }

  // Check custom icons first
  const customIcon = customIcons[iconName]
  if (customIcon) {
    const asLucide = customIcon as unknown as LucideIcon
    iconComponentCache.set(iconName, asLucide)
    return asLucide
  }

  const icons = LucideIcons as Record<string, unknown>
  const icon = icons[iconName]
  const component = icon && (typeof icon === 'function' || typeof icon === 'object') ? icon as LucideIcon : undefined

  if (component) {
    iconComponentCache.set(iconName, component)
  }

  return component
}

/**
 * Converts a {@link Tab} into a {@link SerializedTab} safe for IPC transfer or JSON storage.
 *
 * @description
 * Strips the non-cloneable React icon component and replaces it with
 * a string name via {@link getIconName}.
 *
 * @param tab - The tab to serialize
 * @returns A plain object safe for `JSON.stringify` and Electron IPC
 *
 * @example
 * const serialized = serializeTab(activeTab)
 * window.windowAPI.tearOffStart(serialized, opts)
 */
export function serializeTab(tab: Tab): SerializedTab {
  return {
    id: tab.id,
    path: tab.path,
    title: tab.title,
    iconName: getIconName(tab.icon),
    lifeSpace: tab.lifeSpace,
    isPinned: tab.isPinned,
    isPreview: tab.isPreview,
  }
}

/**
 * Restores a {@link SerializedTab} back into a full {@link Tab} with its icon component.
 *
 * @description
 * Resolves the `iconName` string back to a Lucide React component via
 * {@link getIconComponent}. Resets `isDirty` to `false` on restore.
 *
 * @param data - The serialized tab data (from IPC or localStorage)
 * @returns A fully hydrated Tab with icon component attached
 *
 * @example
 * const tab = deserializeTab(serializedTab)
 * openTab(tab)
 */
export function deserializeTab(data: SerializedTab): Tab {
  return {
    id: data.id,
    path: data.path,
    title: data.title,
    icon: getIconComponent(data.iconName),
    lifeSpace: data.lifeSpace,
    isPinned: data.isPinned,
    isPreview: data.isPreview,
    isDirty: false,
  }
}
